/**
 * Shared Import Constants
 *
 * Single source of truth for CSV header aliases and source fingerprint definitions.
 * Used by both `formatDetection.ts` (format detection) and `universalCSVParser.ts` (CSV parsing).
 */
import type { ImportSource } from '@/types/card';

// ============================================================================
// Format Category
// ============================================================================

export type FormatCategory = 'collection' | 'deck' | 'list' | 'backup';

// ============================================================================
// Canonical Field Names
// ============================================================================

/**
 * Canonical field names — all CSV headers map to one of these.
 * Import-relevant fields produce ImportCard fields.
 * Metadata fields are tracked for field summary but not used in cards.
 */
export type CanonicalField =
  | 'name'
  | 'setCode'
  | 'collectorNumber'
  | 'quantity'
  | 'condition'
  | 'finish'
  | 'foilEtched'
  | 'language'
  | 'purchasePrice'
  | 'purchaseCurrency'
  | 'purchaseDate'
  | 'folderName'
  | 'isAltered'
  | 'isPlaytest'
  | 'sourceId'
  | 'scryfallId'
  // Metadata fields (tracked but not mapped to ImportCard)
  | 'tradeQuantity'
  | 'setName'
  | 'cardType'
  | 'manaCost'
  | 'multiverseId'
  | 'tcgplayerProductId'
  | 'tcgplayerSku'
  | 'cardmarketId'
  | 'creationDate'
  | 'priceLow'
  | 'priceMid'
  | 'priceMarket'
  | 'priceTotal'
  | 'tags'
  | 'lastModified'
  | 'proxy';

/** Fields that map to ImportCard properties (not metadata) */
export const IMPORT_FIELDS = new Set<CanonicalField>([
  'name',
  'setCode',
  'collectorNumber',
  'quantity',
  'condition',
  'finish',
  'foilEtched',
  'language',
  'purchasePrice',
  'purchaseCurrency',
  'purchaseDate',
  'folderName',
  'isAltered',
  'isPlaytest',
  'sourceId',
  'scryfallId',
]);

// ============================================================================
// CSV Header Alias Map
// ============================================================================

/**
 * Maps all known CSV header names (lowercased) to canonical field names.
 * Used for CSV source fingerprinting during detection and header normalization during parsing.
 */
export const HEADER_ALIASES: Record<string, CanonicalField> = {
  // Core fields
  name: 'name',
  'card name': 'name',
  cardname: 'name',

  // Set / collector
  'set code': 'setCode',
  'edition code': 'setCode',
  edition: 'setCode',
  'card number': 'collectorNumber',
  'collector number': 'collectorNumber',
  "collector's number": 'collectorNumber',

  // Quantity
  quantity: 'quantity',
  count: 'quantity',
  qty: 'quantity',

  // Condition / finish / language
  condition: 'condition',
  printing: 'finish',
  foil: 'finish',
  'foil/etched': 'foilEtched',
  language: 'language',

  // Purchase
  'price bought': 'purchasePrice',
  'purchase price': 'purchasePrice',
  'acquired price': 'purchasePrice',
  currency: 'purchaseCurrency',
  'date bought': 'purchaseDate',

  // Organization
  'folder name': 'folderName',
  'list name': 'folderName',

  // Flags
  alter: 'isAltered',
  'playtest card': 'isPlaytest',

  // Source IDs
  'delver local id': 'sourceId',
  'scryfall id': 'scryfallId',

  // Metadata (tracked for fingerprinting, not used for import)
  'trade quantity': 'tradeQuantity',
  'tradelist count': 'tradeQuantity',
  'set name': 'setName',
  'card type': 'cardType',
  'mana cost': 'manaCost',
  multiverseid: 'multiverseId',
  'tcgplayer productid': 'tcgplayerProductId',
  'tcgplayer sku': 'tcgplayerSku',
  'cardmarket id': 'cardmarketId',
  'creation date': 'creationDate',
  low: 'priceLow',
  mid: 'priceMid',
  market: 'priceMarket',
  'min price (market)': 'priceMarket',
  price: 'priceMarket',
  'price (total)': 'priceTotal',
  tags: 'tags',
  'last modified': 'lastModified',
  proxy: 'proxy',
};

// ============================================================================
// Source Fingerprints (detection priority order — most specific first)
// ============================================================================

export interface SourceFingerprint {
  source: ImportSource;
  category: FormatCategory;
  format: string;
  requiredFields: CanonicalField[];
  excludedFields?: CanonicalField[];
  extraCheck?: (text: string) => boolean;
}

export const SOURCE_FINGERPRINTS: SourceFingerprint[] = [
  // 1. Dragon Shield collection: folderName + tradeQuantity
  {
    source: 'dragonshield',
    category: 'collection',
    format: 'dragonshield-csv',
    requiredFields: ['folderName', 'tradeQuantity'],
    extraCheck: (text) => text.slice(0, 500).includes('sep='),
  },
  // 2. DelverLens v2: sourceId + setCode
  {
    source: 'delverlens',
    category: 'collection',
    format: 'delverlens-v2-csv',
    requiredFields: ['sourceId', 'setCode'],
  },
  // 3. Moxfield: collectorNumber + isAltered (Alter header is always present in Moxfield CSVs)
  {
    source: 'moxfield',
    category: 'collection',
    format: 'moxfield-csv',
    requiredFields: ['collectorNumber', 'isAltered'],
  },
  // 4. DelverLens v1: setCode + collectorNumber - sourceId
  {
    source: 'delverlens',
    category: 'collection',
    format: 'delverlens-v1-csv',
    requiredFields: ['setCode', 'collectorNumber'],
    excludedFields: ['sourceId'],
  },
  // 5. Dragon Shield deck: cardType + name + quantity + manaCost
  {
    source: 'dragonshield',
    category: 'deck',
    format: 'dragonshield-deck-csv',
    requiredFields: ['cardType', 'name', 'quantity', 'manaCost'],
  },
  // 6. DelverLens/Moxfield deck CSV: all 7 quoted headers must match exactly
  {
    source: 'moxfield',
    category: 'deck',
    format: 'delverlens-moxfield-deck-csv',
    requiredFields: ['quantity', 'name', 'setCode', 'condition', 'language', 'finish', 'collectorNumber'],
    excludedFields: ['folderName', 'tradeQuantity', 'sourceId', 'isPlaytest', 'cardType', 'manaCost'],
    extraCheck: (text) => {
      const firstLine = text.split(/\r?\n/)[0] ?? '';
      return firstLine.includes('"');
    },
  },
  // 7. Moxfield fallback: setCode + quantity
  {
    source: 'moxfield',
    category: 'collection',
    format: 'moxfield-fallback-csv',
    requiredFields: ['setCode', 'quantity'],
    excludedFields: ['folderName', 'sourceId'],
  },
];
